import api from '../../api';
import { deliverBlobToUser, normalizeDownloadBlob } from '../../../utils/downloadBlob';
import {
  buildGaisukeArenaVideoDownloadFilename,
  parseFilenameFromContentDisposition,
} from '../../../utils/gaisukeArenaVideoDownload';
import { resolveArenaWeeklyMediaLocale } from '../../../utils/gaisukeArenaMediaLocale';
import { youtubeVideoId } from '../../../utils/youtubeEmbed';

const BASE = '/gaisuke';

export type GaisukeTimeframe = '1d' | '1wk' | '4h' | '1h';

export interface GaisukeCandle {
  ts: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number | null;
}

export interface GaisukeChartData {
  symbol: string;
  timeframe: GaisukeTimeframe;
  candles: GaisukeCandle[];
  lastUpdated: string | null;
}

export interface GaisukeYieldPoint {
  seriesId: string;
  ts: string;
  value: number;
}

export interface GaisukeNewsItem {
  id: number;
  publishedAt: string;
  source: string;
  title: string;
  summary: string;
  impactScore: number;
  url: string | null;
}

export interface GaisukePortfolio {
  cashJpy: number;
  initialCash: number;
  baseCurrency: string;
  position: {
    side: 'long' | 'short';
    quantity: number;
    avgPrice: number;
    unrealizedPnl: number;
  } | null;
  unrealizedPnl: number;
  totalEquity: number;
  tradingWindowOpen: boolean;
  tradingWindowLabel: string;
}

export interface GaisukeTrade {
  id: number;
  side: 'buy' | 'sell';
  quantity: number;
  entryPrice: number;
  exitPrice: number | null;
  pnl: number | null;
  openedAt: string;
  closedAt: string | null;
  signalId: number | null;
}

export interface GaisukeSignal {
  id: number;
  side: 'buy' | 'sell' | 'hold';
  strength: number;
  reason: string;
  strategy: string;
  executed: boolean;
  createdAt: string;
}

export interface GaisukeBacktestResult {
  strategy: string;
  days: number;
  totalTrades: number;
  winRate: number;
  totalPnl: number;
  maxDrawdown: number;
}

export const GAISUKE_SYMBOLS = {
  USDJPY: 'USDJPY=X',
  EURUSD: 'EURUSD=X',
  N225: '^N225',
  GSPC: '^GSPC',
  JP10Y: 'JP10Y=X',
} as const;

export async function fetchGaisukeStatus() {
  const { data } = await api.get(`${BASE}/status`);
  return data.data;
}

export async function fetchGaisukeChart(symbol: string, timeframe: GaisukeTimeframe) {
  const { data } = await api.get(`${BASE}/chart/${encodeURIComponent(symbol)}`, {
    params: { timeframe },
  });
  return data.data as GaisukeChartData;
}

export async function fetchGaisukeYields(seriesId?: string) {
  const { data } = await api.get(`${BASE}/yields`, { params: seriesId ? { seriesId } : {} });
  return data.data as GaisukeYieldPoint[];
}

export async function fetchGaisukeNews() {
  const { data } = await api.get(`${BASE}/news`);
  return data.data as GaisukeNewsItem[];
}

export async function fetchGaisukePortfolio() {
  const { data } = await api.get(`${BASE}/portfolio`);
  return data.data as GaisukePortfolio;
}

export async function postGaisukeTrade(side: 'buy' | 'sell', quantity: number) {
  const { data } = await api.post(`${BASE}/trades`, { side, quantity });
  return data.data as GaisukeTrade;
}

export async function fetchGaisukeTrades() {
  const { data } = await api.get(`${BASE}/trades`);
  return data.data as GaisukeTrade[];
}

export async function fetchGaisukeSignals() {
  const { data } = await api.get(`${BASE}/signals`);
  return data.data as GaisukeSignal[];
}

export async function postGaisukeGenerateSignal() {
  const { data } = await api.post(`${BASE}/signals/generate`);
  return data.data as GaisukeSignal | null;
}

export async function fetchGaisukeBacktest(days: number, strategy: string) {
  const { data } = await api.get(`${BASE}/backtest`, { params: { days, strategy } });
  return data.data as GaisukeBacktestResult;
}

export async function postGaisukeRefreshMarket() {
  const { data } = await api.post(`${BASE}/market/refresh`);
  return data;
}

export interface GaisukeDailyBriefing {
  id: number;
  briefingDate: string;
  version: number;
  isLatest: boolean;
  placeLimitOrder: boolean;
  recommendedSide: 'buy' | 'sell' | 'none';
  limitPrice: number | null;
  quantity: number | null;
  meetingNotes: string;
  previousTradeReview: string;
  reflection: string;
  improvements: string;
  meetingMinutes: string;
  llmModel: string;
  createdAt: string;
}

export interface GaisukeLimitOrder {
  id: number;
  side: 'buy' | 'sell';
  limitPrice: number;
  quantity: number;
  status: 'pending' | 'filled' | 'cancelled';
  filledAt: string | null;
  tradeId: number | null;
  createdAt: string;
}

export interface GaisukeMorningLlmBudget {
  llmGenerateMaxSec: number;
  morningLlmModel: string;
  minSec: number;
  maxSec: number;
  stepSec: number;
  defaultSec: number;
}

export interface GaisukeMorningMeeting {
  briefing: GaisukeDailyBriefing | null;
  pendingLimitOrders: GaisukeLimitOrder[];
  jstDate: string;
  history: GaisukeDailyBriefing[];
  llmBudget: GaisukeMorningLlmBudget;
}

export async function fetchGaisukeMorningMeeting() {
  const { data } = await api.get(`${BASE}/morning-meeting`);
  return data.data as GaisukeMorningMeeting;
}

export async function postGaisukeMorningBriefing(llmGenerateMaxSec = 60) {
  const timeoutMs = (llmGenerateMaxSec + 5) * 1000;
  const { data } = await api.post(
    `${BASE}/morning-meeting/generate`,
    { llmGenerateMaxSec },
    { timeout: timeoutMs }
  );
  return data.data as GaisukeMorningMeeting;
}

export async function patchGaisukeMorningLlmBudget(llmGenerateMaxSec: number) {
  const { data } = await api.patch(`${BASE}/morning-meeting/llm-budget`, { llmGenerateMaxSec });
  return data.data as GaisukeMorningLlmBudget;
}

export async function postGaisukeCancelLimitOrder(orderId: number) {
  const { data } = await api.post(`${BASE}/limit-orders/${orderId}/cancel`);
  return data.data as GaisukeLimitOrder;
}

export async function patchGaisukeMeetingMinutes(briefingId: number, meetingMinutes: string) {
  const { data } = await api.patch(`${BASE}/morning-meeting/minutes`, { briefingId, meetingMinutes });
  return data.data as GaisukeDailyBriefing;
}

export const GAISUKE_QUOTE_POLL_MS = 30_000;

export interface GaisukeQuote {
  symbol: string;
  price: number;
  previousClose: number | null;
  change: number | null;
  changePct: number | null;
  asOf: string;
}

export interface GaisukeRecommendationTrade {
  limitOrderId: number;
  tradeId: number | null;
  briefingDate: string;
  briefingVersion: number;
  side: 'buy' | 'sell';
  limitPrice: number;
  quantity: number;
  filledAt: string;
  pnl: number | null;
  cumulativeRealizedPnl: number;
  isOpen: boolean;
}

export interface GaisukeRecommendationPerformance {
  realizedPnl: number;
  unrealizedPnl: number;
  totalPnl: number;
  initialCash: number;
  returnPct: number;
  filledOrderCount: number;
  history: GaisukeRecommendationTrade[];
}

export async function fetchGaisukeQuote(symbol: string) {
  const { data } = await api.get(`${BASE}/quote/${encodeURIComponent(symbol)}`);
  return data.data as GaisukeQuote;
}

export async function fetchGaisukeRecommendationPerformance() {
  const { data } = await api.get(`${BASE}/recommendation-performance`);
  return data.data as GaisukeRecommendationPerformance;
}

export interface GaisukeLlmCostEstimate {
  modelId: string;
  usdPerCall: number;
  usdPerMonth: number;
  jpyPerMonth: number;
  inputTokensPerCall: number;
  outputTokensPerCall: number;
  callsPerMonth: number;
  pricingKnown: boolean;
}

export interface GaisukeModelWatch {
  configuredModel: string;
  morningLlmModel: string;
  morningModelListed: boolean;
  apiKeyConfigured: boolean;
  rollingAlias: boolean;
  newerModels: string[];
  unknownModels: string[];
  hasAnnouncement: boolean;
  message: string;
  checkedAt: string;
  suggestedModel: string | null;
  currentCost: GaisukeLlmCostEstimate | null;
  suggestedCost: GaisukeLlmCostEstimate | null;
  autoApplied: boolean;
  autoApplySkippedReason: string | null;
  previousModel: string | null;
}

export async function fetchGaisukeModelWatch() {
  const { data } = await api.get(`${BASE}/model-watch`);
  return data.data as GaisukeModelWatch;
}

export interface GaisukeArenaPeriodChampion {
  periodKey: 'week' | 'month' | 'year';
  armId: number;
  name: string;
  provider: string;
  returnPct: number;
  winRatePct: number;
  pnlJpy: number;
}

export interface GaisukeArenaPeriodChampions {
  week: GaisukeArenaPeriodChampion | null;
  month: GaisukeArenaPeriodChampion | null;
  year: GaisukeArenaPeriodChampion | null;
}

export interface GaisukeArenaEquityGapStatus {
  ratio: number | null;
  thresholdRatio: number;
  graceDays: number;
  firstDetectedAt: string | null;
  graceElapsedDays: number | null;
  graceRemainingDays: number | null;
  graceActive: boolean;
}

export interface GaisukeArenaStatus {
  experimentId: number;
  name: string;
  roundNumber: number;
  experimentDayNumber: number;
  startDate: string;
  endDate: string;
  status: string;
  daysRemaining: number;
  activeArmCount: number;
  periodChampions: GaisukeArenaPeriodChampions;
  equityGap: GaisukeArenaEquityGapStatus;
  jstDate: string;
}

export interface GaisukeArenaLeaderboardEntry {
  rank: number;
  armId: number;
  name: string;
  provider: string;
  model: string;
  equityJpy: number;
  cumulativePnlJpy: number;
  returnPct: number;
  maxDrawdownPct: number;
  sharpe: number;
  sortino: number;
  llmCostUsd: number;
  llmCostJpy: number;
  netPnlJpy: number;
}

export interface GaisukeArenaDailyLogEntry {
  id: number;
  armId: number;
  armName: string;
  provider: string;
  model: string;
  snapshotDate: string;
  equityJpy: number;
  dailyPnlJpy: number;
  cumulativePnlJpy: number;
  dayNote: string;
  actionsSummary: string;
  decisionStatus: string;
  tradesFilled?: number;
  tradesUnfilled?: number;
}

export interface GaisukeArenaArmDetail {
  armId: number;
  name: string;
  provider: string;
  model: string;
  active: boolean;
  initialCashJpy: number;
  cashJpy: number;
  equityJpy: number;
  cumulativePnlJpy: number;
  cumulativeLlmCostUsd: number;
  usdJpy: number;
  eurJpy: number;
  holdings: Array<{
    symbol: string;
    displayName: string;
    quantity: number;
    currency: string;
    avgPriceJpy: number;
    marketPriceJpy: number;
    avgPriceNative: number;
    marketPriceNative: number;
    valueJpy: number;
    valueNative: number;
    unrealizedPnlJpy: number;
    unrealizedPnlNative: number;
  }>;
  fx: {
    side: 'long' | 'short';
    notionalUsd: number;
    avgRate: number;
    marketRate: number;
    valueJpy: number;
    unrealizedPnlJpy: number;
  } | null;
  recentDecisions: Array<{
    tradeDate: string;
    status: string;
    dayNote: string;
    costUsd: number;
    errorMessage: string | null;
    parsedActions: unknown;
  }>;
  recentTrades: Array<{
    tradeDate: string;
    symbol: string;
    side: string;
    quantity: number;
    priceJpy: number;
    valueJpy: number;
    pnlJpy: number | null;
  }>;
}

export interface GaisukeArenaManufacturerPanel {
  armId: number;
  name: string;
  provider: string;
  model: string;
  equityJpy: number;
  cumulativePnlJpy: number;
  todayPolicyStatus: string;
  todayPolicySummary: string;
  billingBlocked: boolean;
  billingReason: 'quota' | 'no_api_key' | null;
  billingPortalUrl: string | null;
  estimatedMonthlyCostJpy: number;
  estimatedMonthlyCostUsd: number;
  cumulativeLlmCostJpy: number;
  morningCallsPerMonth: number;
}

export interface GaisukeArenaEquityPoint {
  date: string;
  equityJpy: number;
  dailyPnlJpy: number;
  cumulativePnlJpy: number;
  tradesFilled?: number;
  tradesUnfilled?: number;
}

export interface GaisukeArenaTradeRow {
  id: number;
  executedAt: string;
  tradeDate: string;
  symbol: string;
  displayName: string;
  side: string;
  quantity: number;
  priceNative: number;
  priceJpy: number;
  valueJpy: number;
  pnlJpy: number | null;
  fillStatus: string;
  currency: string;
  jpyPerUnit: number;
  region: string;
}

export interface GaisukeArenaMorningPolicy {
  id: number;
  policyDate: string;
  executionDate: string;
  status: string;
  dayNote: string;
  actionsSummary: string;
  parsedActions: unknown;
  costUsd: number;
  errorMessage: string | null;
  dailyExecutions?: Array<{ date: string; filled: number; unfilled: number; rejected?: number }>;
}

export async function fetchGaisukeArenaStatus() {
  const { data } = await api.get(`${BASE}/arena/status`);
  return data.data as GaisukeArenaStatus;
}

export async function fetchGaisukeArenaLeaderboard() {
  const { data } = await api.get(`${BASE}/arena/leaderboard`);
  return data.data as GaisukeArenaLeaderboardEntry[];
}

export async function fetchGaisukeArenaDailyLog(params?: {
  from?: string;
  to?: string;
  armId?: number;
}) {
  const { data } = await api.get(`${BASE}/arena/daily-log`, { params });
  return data.data as GaisukeArenaDailyLogEntry[];
}

export async function fetchGaisukeArenaArmDetail(armId: number) {
  const { data } = await api.get(`${BASE}/arena/arms/${armId}`);
  return data.data as GaisukeArenaArmDetail;
}

export async function fetchGaisukeArenaManufacturers() {
  const { data } = await api.get(`${BASE}/arena/manufacturers`);
  return data.data as GaisukeArenaManufacturerPanel[];
}

export async function fetchGaisukeArenaBenchmarkIndexCharts() {
  const { data } = await api.get(`${BASE}/arena/benchmark-index-charts`);
  return data.data as {
    charts: GaisukeArenaBenchmarkIndexChart[];
  };
}

export interface GaisukeArenaBenchmarkIndexChart extends GaisukeChartData {
  currency: string;
  displayName: string;
  periodReturn: {
    startDate: string;
    startPrice: number | null;
    endPrice: number | null;
    returnPct: number | null;
  };
}

export async function fetchGaisukeArenaEquitySeries(armId: number, limit = 365) {
  const { data } = await api.get(`${BASE}/arena/arms/${armId}/equity-series`, {
    params: { limit },
  });
  return data.data as GaisukeArenaEquityPoint[];
}

export async function fetchGaisukeArenaTrades(armId: number, limit = 100) {
  const { data } = await api.get(`${BASE}/arena/arms/${armId}/trades`, { params: { limit } });
  return data.data as GaisukeArenaTradeRow[];
}

export async function fetchGaisukeArenaMorningPolicies(armId: number, limit = 60, lang?: string) {
  const { data } = await api.get(`${BASE}/arena/arms/${armId}/morning-policies`, {
    params: { limit, ...(lang ? { lang } : {}) },
  });
  return data.data as GaisukeArenaMorningPolicy[];
}

export interface GaisukeArenaScheduleSlot {
  weekday: number;
  hour: number;
  minute: number;
}

export interface GaisukeArenaSchedule {
  morningMeeting: GaisukeArenaScheduleSlot;
  weeklyReport: GaisukeArenaScheduleSlot;
  weeklyVideo: GaisukeArenaScheduleSlot;
  timezone: string;
}

export interface GaisukeArenaSettings {
  modelMaxJpyMonth: number;
  modelAutoUpgrade: boolean;
  schedule: GaisukeArenaSchedule;
  scheduleDefaults: GaisukeArenaSchedule;
  minModelMaxJpyMonth: number;
  maxModelMaxJpyMonth: number;
  stepModelMaxJpyMonth: number;
  defaultModelMaxJpyMonth: number;
  envDefaultModelMaxJpyMonth: number;
  videoBgmFileName: string | null;
  videoBgmOptions: GaisukeArenaVideoBgmOption[];
  lyriaEnabled: boolean;
  weeklyMediaChainEnabled: boolean;
  videoEnabled: boolean;
}

export interface GaisukeArenaVideoBgmOption {
  fileName: string;
  durationSec: number;
  uploadedAt?: string;
}

export interface GaisukeArenaVideoGenerationLogLine {
  at: string;
  level: 'info' | 'warn' | 'error';
  message: string;
}

export interface GaisukeArenaVideoAdminStatus {
  reportId: number | null;
  weekStartDate: string | null;
  weekEndDate: string | null;
  videoStatus: string | null;
  videoErrorMessage: string | null;
  videoGeneratedAt: string | null;
  running: boolean;
  logs: GaisukeArenaVideoGenerationLogLine[];
  videoBgmFileName: string | null;
  videoBgmOptions: GaisukeArenaVideoBgmOption[];
}

export async function fetchGaisukeArenaSettings() {
  const { data } = await api.get(`${BASE}/arena/settings`);
  return data.data as GaisukeArenaSettings;
}

export async function patchGaisukeArenaSettings(body: {
  modelMaxJpyMonth?: number;
  modelAutoUpgrade?: boolean;
  schedule?: Partial<GaisukeArenaSchedule>;
  videoBgmFileName?: string | null;
}) {
  const { data } = await api.patch(`${BASE}/arena/settings`, body);
  return data.data as GaisukeArenaSettings;
}

export async function fetchGaisukeArenaVideoAdminStatus(reportId?: number) {
  const { data } = await api.get(`${BASE}/arena/video/admin`, {
    params: reportId != null ? { reportId } : undefined,
  });
  return data.data as GaisukeArenaVideoAdminStatus;
}

export async function postGaisukeArenaVideoGenerateAdmin(body: {
  reportId?: number;
  bgmFileName?: string | null;
  assemblyOnly?: boolean;
}) {
  const { data } = await api.post(`${BASE}/arena/video/generate`, body);
  return data.data as { started: boolean; reportId: number; alreadyRunning: boolean };
}

export async function fetchGaisukeArenaBgmPool() {
  const { data } = await api.get(`${BASE}/arena/video/bgm`);
  return data.data as GaisukeArenaVideoBgmOption[];
}

/** BGM 試聴用 blob URL（呼び出し側で revoke すること） */
export async function fetchGaisukeArenaBgmBlobUrl(fileName: string): Promise<string> {
  const res = await api.get(`${BASE}/arena/video/bgm/stream`, {
    params: { fileName },
    responseType: 'blob',
  });
  return window.URL.createObjectURL(res.data as Blob);
}

export async function postGaisukeArenaBgmUpload(params: {
  file: File;
  displayName: string;
  overwrite?: boolean;
}) {
  const formData = new FormData();
  formData.append('file', params.file);
  formData.append('displayName', params.displayName);
  if (params.overwrite) {
    formData.append('overwrite', 'true');
  }
  const { data } = await api.post(`${BASE}/arena/video/bgm/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120_000,
  });
  return data.data as GaisukeArenaVideoBgmOption;
}

export async function postGaisukeArenaBgmRemove(fileName: string) {
  await api.post(`${BASE}/arena/video/bgm/remove`, { fileName });
}

export async function postGaisukeArenaRunMorning() {
  const { data } = await api.post(`${BASE}/arena/run-morning`);
  return data.data as { processed: number; errors: number };
}

export async function postGaisukeArenaRunExecution() {
  const { data } = await api.post(`${BASE}/arena/run-execution`);
  return data.data as { processed: number; errors: number };
}

export async function postGaisukeArenaRunDaily() {
  const { data } = await api.post(`${BASE}/arena/run-daily`);
  return data.data as { processed: number; errors: number };
}

export async function patchGaisukeArenaArm(armId: number, active: boolean) {
  await api.patch(`${BASE}/arena/arms/${armId}`, { active });
}

export interface GaisukeArenaLatestMedia {
  pdf: {
    id: number;
    weekStartDate: string;
    weekEndDate: string;
    hasPdf: boolean;
    hasPdfEn?: boolean;
  } | null;
  video: {
    id: number;
    weekStartDate: string;
    weekEndDate: string;
    hasVideo: boolean;
    hasVideoEn?: boolean;
    videoStatus: string;
    videoTitleTelopJa?: string | null;
    videoTitleTelopEn?: string | null;
    experimentDayNumber?: number | null;
    experimentRoundNumber?: number | null;
  } | null;
}

export function gaisukeArenaReportHasVideoForLocale(
  r: { videoStatus: string; hasVideo: boolean; hasVideoEn?: boolean },
  uiLanguage: string
): boolean {
  if (r.videoStatus !== 'done') return false;
  const locale = resolveArenaWeeklyMediaLocale(uiLanguage);
  if (locale === 'en') return Boolean(r.hasVideoEn);
  return Boolean(r.hasVideo);
}

export interface GaisukeArenaWeeklyReport {
  id: number;
  weekStartDate: string;
  weekEndDate: string;
  status: string;
  hasPdf: boolean;
  hasPdfEn?: boolean;
  hasVideo: boolean;
  hasVideoEn?: boolean;
  videoStatus: string;
  llmModel: string;
  costUsd: number;
  videoModel: string;
  videoCostUsd: number;
  errorMessage: string | null;
  videoErrorMessage: string | null;
  generatedAt: string | null;
  videoGeneratedAt: string | null;
  createdAt: string;
  videoTitleTelopJa?: string | null;
  videoTitleTelopEn?: string | null;
  experimentDayNumber?: number | null;
  experimentRoundNumber?: number | null;
  /** 投稿済み YouTube 動画 ID。サイト上の再生は埋め込みが正（mp4 は管理者DL用に残す） */
  youtubeVideoIdJa?: string | null;
  youtubeVideoIdEn?: string | null;
}

/**
 * その言語で投稿済みの YouTube 動画 ID。
 * 他言語へフォールバックしない — 英語 UI に日本語音声の動画を出すより、
 * 英語版 mp4（`hasVideoEn`）で再生させるほうが正しい。
 * `uiLanguage` は `en-US` 等でもよい（`resolveArenaWeeklyMediaLocale` で正規化）。
 */
export function gaisukeArenaReportYoutubeVideoId(
  r: GaisukeArenaWeeklyReport,
  uiLanguage: string
): string | null {
  const locale = resolveArenaWeeklyMediaLocale(uiLanguage);
  const raw = (locale === 'en' ? r.youtubeVideoIdEn : r.youtubeVideoIdJa) ?? null;
  // 不正・空 ID は埋め込み不可。canPlay が true なのにプレイヤーが null になるのを防ぐ
  return youtubeVideoId(raw);
}

export async function fetchGaisukeArenaReports() {
  const { data } = await api.get(`${BASE}/arena/reports`);
  return data.data as GaisukeArenaWeeklyReport[];
}

export async function fetchGaisukeArenaLatestMedia() {
  const { data } = await api.get(`${BASE}/arena/reports/latest`);
  return data.data as GaisukeArenaLatestMedia;
}

function arenaReportPdfFilename(reportId: number, lang: string | undefined, header?: string): string {
  return (
    parseFilenameFromContentDisposition(header) ??
    `gaisuke-arena-weekly-report-${reportId}${lang ? `-${lang}` : ''}.pdf`
  );
}

/**
 * 週次レポート PDF を認証付きで取得し、新しいタブで開く。
 * ※ Chrome は blob: URL 上の組み込み PDF ビューアからの「ダウンロード」が
 *    接続エラーになることがある。保存したい場合は downloadGaisukeArenaReportPdf を使う。
 */
export async function openGaisukeArenaReportPdf(reportId: number, lang?: string) {
  const params = lang ? { lang } : undefined;
  const res = await api.get(`${BASE}/arena/reports/${reportId}/pdf`, {
    params,
    responseType: 'blob',
  });
  const blob = normalizeDownloadBlob(res.data as Blob, 'application/pdf');
  const url = window.URL.createObjectURL(blob);
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (!opened) {
    // ポップアップ阻害時はファイル名付きダウンロードへフォールバック
    deliverBlobToUser(
      blob,
      arenaReportPdfFilename(reportId, lang, res.headers['content-disposition'] as string | undefined)
    );
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1_000);
    return;
  }
  // 表示中の閲覧を想定し、すぐ revoke しない（Chrome の blob タブ保存は別問題）
  window.setTimeout(() => window.URL.revokeObjectURL(url), 15 * 60_000);
}

/** 週次レポート PDF を表示名付きで保存（blob タブの「ダウンロード」失敗を避ける） */
export async function downloadGaisukeArenaReportPdf(reportId: number, lang?: string): Promise<void> {
  const params = lang ? { lang } : undefined;
  const res = await api.get(`${BASE}/arena/reports/${reportId}/pdf`, {
    params,
    responseType: 'blob',
  });
  const blob = normalizeDownloadBlob(res.data as Blob, 'application/pdf');
  deliverBlobToUser(
    blob,
    arenaReportPdfFilename(reportId, lang, res.headers['content-disposition'] as string | undefined)
  );
}

export async function postGaisukeArenaGenerateReport(options?: { chainVideo?: boolean }) {
  const params =
    options?.chainVideo === true ? { chainVideo: 'true' as const } : undefined;
  const { data } = await api.post(`${BASE}/arena/reports/generate`, undefined, { params });
  return data.data as GaisukeArenaWeeklyReport;
}

/** 週次動画を認証付き blob URL で取得（呼び出し側で revoke すること） */
export async function fetchGaisukeArenaReportVideoBlobUrl(
  reportId: number,
  lang?: string,
  cacheBuster?: string | null
): Promise<string> {
  const params: Record<string, string> = {};
  if (lang) params.lang = lang;
  if (cacheBuster) params.v = cacheBuster;
  const res = await api.get(`${BASE}/arena/reports/${reportId}/video`, {
    params,
    responseType: 'blob',
  });
  return window.URL.createObjectURL(res.data as Blob);
}

/** 週次動画を新しいタブで開く */
export async function openGaisukeArenaReportVideo(reportId: number, lang?: string) {
  const url = await fetchGaisukeArenaReportVideoBlobUrl(reportId, lang);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
}

/** 週次動画を表示名付きでダウンロード */
export async function downloadGaisukeArenaReportVideo(
  reportId: number,
  lang?: string,
  suggestedFilename?: string
): Promise<void> {
  const params: Record<string, string> = { download: '1' };
  if (lang) params.lang = lang;
  const res = await api.get(`${BASE}/arena/reports/${reportId}/video`, {
    params,
    responseType: 'blob',
  });
  const header = res.headers['content-disposition'] as string | undefined;
  const filename =
    parseFilenameFromContentDisposition(header) ??
    suggestedFilename ??
    buildGaisukeArenaVideoDownloadFilename({
      weekStartDate: 'video',
      weekEndDate: String(reportId),
      lang: lang ?? 'ja',
    });
  deliverBlobToUser(res.data as Blob, filename);
}

/** 公開最新動画を表示名付きでダウンロード（システム管理者・要認証） */
export async function downloadGaisukeArenaPublicVideo(
  lang = 'ja',
  suggestedFilename?: string
): Promise<void> {
  const res = await api.get(`${BASE}/public/arena/latest/video`, {
    params: { lang, download: '1' },
    responseType: 'blob',
  });
  const header = res.headers['content-disposition'] as string | undefined;
  const filename =
    parseFilenameFromContentDisposition(header) ??
    suggestedFilename ??
    buildGaisukeArenaVideoDownloadFilename({
      weekStartDate: 'latest',
      weekEndDate: lang,
      lang,
    });
  deliverBlobToUser(res.data as Blob, filename);
}

export async function postGaisukeArenaGenerateReportVideo(reportId: number) {
  const { data } = await api.post(`${BASE}/arena/reports/${reportId}/generate-video`);
  return data.data as GaisukeArenaWeeklyReport;
}

export interface GaisukeArenaPublicLatest {
  weekStartDate: string;
  weekEndDate: string;
  titleTelop: string | null;
  experimentDayNumber: number | null;
  experimentRoundNumber: number | null;
  disclaimer: string;
  hasVideoJa: boolean;
  hasVideoEn: boolean;
  hasPdfJa: boolean;
  hasPdfEn: boolean;
  hasThumbnailJa: boolean;
  hasThumbnailEn: boolean;
  youtubeVideoIdJa: string | null;
  youtubeVideoIdEn: string | null;
  videoGeneratedAt: string | null;
}

function publicApiBase(): string {
  if (import.meta.env.VITE_API_URL) return import.meta.env.VITE_API_URL.replace(/\/$/, '');
  return '/api';
}

export function gaisukeArenaPublicVideoUrl(lang = 'ja', cacheBuster?: string | null): string {
  const base = `${publicApiBase()}/gaisuke/public/arena/latest/video?lang=${encodeURIComponent(lang)}`;
  if (!cacheBuster) return base;
  return `${base}&v=${encodeURIComponent(cacheBuster)}`;
}

export function gaisukeArenaPublicPdfUrl(lang = 'ja'): string {
  return `${publicApiBase()}/gaisuke/public/arena/latest/pdf?lang=${encodeURIComponent(lang)}`;
}

export function gaisukeArenaPublicThumbnailUrl(lang = 'ja', cacheBuster?: string | null): string {
  const base = `${publicApiBase()}/gaisuke/public/arena/latest/thumbnail?lang=${encodeURIComponent(lang)}`;
  if (!cacheBuster) return base;
  return `${base}&v=${encodeURIComponent(cacheBuster)}`;
}

export function gaisukeArenaPublicWeekVideoUrl(
  weekStart: string,
  lang = 'ja',
  cacheBuster?: string | null
): string {
  const base = `${publicApiBase()}/gaisuke/public/arena/week/${encodeURIComponent(weekStart)}/video?lang=${encodeURIComponent(lang)}`;
  if (!cacheBuster) return base;
  return `${base}&v=${encodeURIComponent(cacheBuster)}`;
}

export function gaisukeArenaPublicWeekPdfUrl(weekStart: string, lang = 'ja'): string {
  return `${publicApiBase()}/gaisuke/public/arena/week/${encodeURIComponent(weekStart)}/pdf?lang=${encodeURIComponent(lang)}`;
}

export function gaisukeArenaPublicWeekThumbnailUrl(
  weekStart: string,
  lang = 'ja',
  cacheBuster?: string | null
): string {
  const base = `${publicApiBase()}/gaisuke/public/arena/week/${encodeURIComponent(weekStart)}/thumbnail?lang=${encodeURIComponent(lang)}`;
  if (!cacheBuster) return base;
  return `${base}&v=${encodeURIComponent(cacheBuster)}`;
}

export async function fetchGaisukeArenaPublicLatest(lang?: string) {
  const params = lang ? { lang } : undefined;
  const { data } = await api.get(`${BASE}/public/arena/latest`, { params });
  return data.data as GaisukeArenaPublicLatest;
}

export async function fetchGaisukeArenaPublicWeek(weekStart: string, lang?: string) {
  const params = lang ? { lang } : undefined;
  const { data } = await api.get(`${BASE}/public/arena/week/${encodeURIComponent(weekStart)}`, {
    params,
  });
  return data.data as GaisukeArenaPublicLatest;
}
